import { createPolled } from "@solid-primitives/timer"
import { useNavigate } from "@solidjs/router"
import { type CreateQueryState, createQuery } from "@urql/solid"
import {
  createMemo,
  createSignal,
  For,
  type JSX,
  Match,
  Show,
  Switch,
} from "solid-js"
import { ulid } from "ulid"
import { PostAttachmentFragment } from "@/components/post-attachment/post-attachment"
import { urqlClient } from "@/context/graphql-provider"
import { createSession } from "@/context/session-provider"
import { Box } from "@/designSystem/components/box"
import { Button, LinkButton } from "@/designSystem/components/button"
import { Dialog } from "@/designSystem/components/dialog"
import { FikaLogo } from "@/designSystem/components/fika-logo"
import { NavAvatar } from "@/designSystem/components/nav-avatar"
import { NavLink } from "@/designSystem/components/nav-link"
import { Text } from "@/designSystem/components/text"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Fireworks } from "@/designSystem/patterns/fireworks"
import { Info } from "@/designSystem/patterns/info"
import { Note, NoteFragment } from "@/designSystem/patterns/note"
import { PostComponent, PostFragment } from "@/designSystem/patterns/post"
import { Stats } from "@/designSystem/patterns/stats"
import {
  createFragment,
  type FragmentOf,
  graphql,
  type ResultOf,
  readFragment,
} from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import {
  PublicationDetailsForm,
  PublicationDetailsFragment,
} from "@/pages/posts/_publication-details-form"
import {
  PublicationLanguageForm,
  PublicationLanguageFormFragment,
} from "@/pages/posts/_publication-language-form"
import { PublicationNameForm } from "@/pages/posts/_publication-name-form"
import {
  ProfileForm,
  UserProfileFragment,
} from "@/pages/settings/_profile-form"
import { createBlogRouter } from "@/routes/posts-router"
import { DashboardSection } from "./_dashboard-section"
import { RecentReactions, RecentReactionsFragment } from "./_recent-reactions"
import {
  bottomBarClass,
  containerClass,
  headerClass,
  navHeaderClass,
  onboardingActionsClass,
  onboardingCardClass,
  onboardingContainerClass,
  onboardingContentClass,
  onboardingDescriptionClass,
  onboardingImageClass,
  onboardingTitleClass,
  statsClass,
  statsContainerClass,
  welcomeContainerClass,
  welcomeNameClass,
  welcomeTextClass,
  wrapperClass,
} from "./dashboard-page.css"
import { EmailVerificationWidget } from "./email-verification-widget"
import rabbitOnboarding from "./rabbit-onboarding.webp"

export function Dashboard() {
  const { LL } = useI18nContext()
  const { session } = createSession()
  const [dashboardQuery] = createQuery({ query: DashboardQuery })
  const isEmailVerified = createMemo(() => {
    const s = session()
    return s.status === "logged-in" ? s.user.emailVerified : true
  })

  return (
    <MainLayout
      hue="sand"
      header={
        <Show when={dashboardQuery.data}>
          {data => <Header data={data()} />}
        </Show>
      }
      footer={
        <Show when={dashboardQuery.data}>
          {data => <MobileNav data={data()} />}
        </Show>
      }
      footerPadded={false}
      query={dashboardQuery}
    >
      {data => {
        const headerData = createFragment(DashboardHeaderFragment, () => data)
        const userName = createMemo(
          () => headerData()?.me?.publication?.name ?? null,
        )
        const authorName = createMemo(
          () => headerData()?.me?.authorName ?? null,
        )
        const welcomeName = createMemo(
          () => authorName() ?? userName() ?? "stranger",
        )

        return (
          <div class={wrapperClass}>
            <div class={welcomeContainerClass}>
              <Text
                fontSize="hero3"
                textAlign="center"
                color="sand800"
                fontFamily="serif"
                fontWeight="700"
                class={welcomeTextClass}
              >
                {LL().dashboard.welcomeBack()},
              </Text>
              <Text
                fontSize="hero2"
                textAlign="center"
                color="sand900"
                fontFamily="serif"
                fontWeight="700"
                class={welcomeNameClass}
              >
                {welcomeName()}
              </Text>
            </div>
            <OnboardingTasks
              onboardingTasksQuery={dashboardQuery}
              data={data}
            />
            <Show when={userName()}>
              <BlogStats data={data} isPending={dashboardQuery.fetching} />
            </Show>
            <Show
              when={userName()}
              fallback={
                <Show
                  when={isEmailVerified()}
                  fallback={<EmailVerificationWidget />}
                >
                  <CreatePublicationWidget />
                </Show>
              }
            >
              <div class={containerClass}>
                <BlogLatest data={data} isPending={dashboardQuery.fetching} />
                <RecentReactions
                  data={data}
                  isPending={dashboardQuery.fetching}
                />
              </div>
              <div class={containerClass}>
                <LastNotes data={data} isPending={dashboardQuery.fetching} />
                <BlogDrafts data={data} isPending={dashboardQuery.fetching} />
              </div>
            </Show>
          </div>
        )
      }}
    </MainLayout>
  )
}

const DashboardHeaderFragment = graphql(`
  fragment DashboardHeaderFragment on Query {
    me {
      id
      authorName
      profileImage
      publication {
        id
        name
      }
    }
  }
`)

const InboxUnreadQuery = graphql(`
  query InboxUnread {
    inbox {
      id
      unreadCount
    }
  }
`)

type HeaderProps = {
  data: FragmentOf<typeof DashboardHeaderFragment>
}

function Header(props: HeaderProps) {
  const { LL } = useI18nContext()

  const data = createFragment(DashboardHeaderFragment, () => props.data)

  const hasBlog = createMemo(() => Boolean(data()?.me?.publication?.name))

  const [inboxResult, reexecuteInbox] = createQuery({ query: InboxUnreadQuery })
  const unreadInboxCount = createMemo(
    () => inboxResult.data?.inbox?.filter(t => t.unreadCount > 0).length ?? 0,
  )
  createPolled(() => reexecuteInbox({ requestPolicy: "network-only" }), 30_000)

  return (
    <nav class={headerClass}>
      <FikaLogo link />
      <div class={navHeaderClass}>
        <Show when={hasBlog()}>
          <NavLink hue="amber" href="/blog" iconName="pen-tool" wrap>
            {LL().blog.title()}
          </NavLink>
        </Show>
        <NavLink hue="indigo" href="/subscriptions" iconName="newspaper" wrap>
          {LL().subscriptions.title()}
        </NavLink>
        <NavLink
          hue="rose"
          href="/inbox"
          iconName="mail"
          wrap
          badge={unreadInboxCount()}
        >
          {LL().inbox.title()}
        </NavLink>
      </div>
      <Show when={data()?.me}>
        {me => (
          <NavAvatar
            href="/settings"
            title={LL().common.settings()}
            profileImage={me()?.profileImage}
          />
        )}
      </Show>
    </nav>
  )
}

function MobileNav(props: HeaderProps) {
  const { LL } = useI18nContext()

  const data = createFragment(DashboardHeaderFragment, () => props.data)
  const hasBlog = createMemo(() => Boolean(data()?.me?.publication?.name))

  const [inboxResult, reexecuteInbox] = createQuery({ query: InboxUnreadQuery })
  const unreadInboxCount = createMemo(
    () => inboxResult.data?.inbox?.filter(t => t.unreadCount > 0).length ?? 0,
  )
  createPolled(() => reexecuteInbox({ requestPolicy: "network-only" }), 30_000)

  return (
    <nav class={bottomBarClass}>
      <Show when={hasBlog()}>
        <NavLink hue="amber" href="/blog" iconName="pen-tool" wrap>
          {LL().blog.title()}
        </NavLink>
      </Show>
      <NavLink hue="indigo" href="/subscriptions" iconName="newspaper" wrap>
        {LL().subscriptions.title()}
      </NavLink>
      <NavLink
        hue="rose"
        href="/inbox"
        iconName="mail"
        wrap
        badge={unreadInboxCount()}
      >
        {LL().inbox.title()}
      </NavLink>
    </nav>
  )
}

const SubscribersStatsFragment = graphql(`
  fragment SubscribersStatsFragment on Query {
    me {
      id
      publication {
        id
        subscriberStats {
          day
          amount
        }
      }
    }
  }
`)

const ViewsStatsFragment = graphql(`
  fragment ViewsStatsFragment on Query {
    me {
      id
      publication {
        id
        dailyViews {
          day
          amount
        }
        viewStats {
          currentPeriodViews
          previousPeriodViews
        }
        openRatesStats {
          currentMonthEmailsSent
          currentMonthOpenRatePercent
          previousMonthOpenRatePercent
        }
      }
    }
  }
`)

const PostsStatsFragment = graphql(`
  fragment PostsStatsFragment on Query {
    postStats(daysAgo: null) {
      day
      amount
    }
  }
`)

function BlogStats(props: {
  data: FragmentOf<typeof SubscribersStatsFragment> &
    FragmentOf<typeof PostsStatsFragment> &
    FragmentOf<typeof ViewsStatsFragment>
  isPending: boolean
}) {
  const subscribersData = createFragment(
    SubscribersStatsFragment,
    () => props.data,
  )
  const postStatsData = createFragment(PostsStatsFragment, () => props.data)
  const viewsData = createFragment(ViewsStatsFragment, () => props.data)

  const hasEmailsSent = createMemo(() => {
    const openRatesStats = viewsData()?.me?.publication?.openRatesStats
    return (openRatesStats?.currentMonthEmailsSent ?? 0) > 0
  })

  return (
    <div class={statsContainerClass}>
      <div class={statsClass}>
        <ViewsStats data={viewsData()} isPending={props.isPending} />
      </div>
      <div class={statsClass}>
        <SubscriberStats data={subscribersData()} isPending={props.isPending} />
      </div>
      <div class={statsClass}>
        <Show
          when={hasEmailsSent()}
          fallback={
            <PostsStats
              postStats={() => postStatsData().postStats}
              isPending={props.isPending}
            />
          }
        >
          <OpenRatesStats data={viewsData()} isPending={props.isPending} />
        </Show>
      </div>
    </div>
  )
}

function SubscriberStats(props: {
  data: ResultOf<typeof SubscribersStatsFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()

  const statsData = createMemo(() => {
    const stats = props.data.me?.publication?.subscriberStats ?? []
    const intervalType = "DAY" as const

    const chartData = stats
      .slice(-30)
      .filter(stat => stat.day)
      .map(stat => ({
        date: stat.day,
        count: stat.amount ?? 0,
      }))

    const count = chartData[chartData.length - 1]?.count ?? 0
    const previousCount = chartData[0]?.count ?? 0

    return {
      count,
      previousCount,
      chartData,
      intervalType,
    }
  })

  return (
    <Stats
      data={statsData()}
      loading={props.isPending}
      hue="orange"
      label={LL().dashboard.subscribers()}
      iconName="users-round"
    />
  )
}

function PostsStats(props: {
  postStats: () => Array<{
    day: string
    amount: number
  }>
  isPending: boolean
}) {
  const { LL } = useI18nContext()

  const statsData = createMemo(() => {
    const stats = props.postStats()
    const intervalType = "DAY" as const

    const chartData = stats
      .filter(stat => stat.day)
      .map(stat => ({
        date: stat.day,
        count: stat.amount ?? 0,
      }))

    const count = chartData[chartData.length - 1]?.count ?? 0

    return {
      count,
      previousCount: 0,
      chartData,
      intervalType,
    }
  })

  return (
    <Stats
      data={statsData()}
      loading={props.isPending}
      hue="orange"
      label={LL().dashboard.posts()}
      iconName="pen-tool"
    />
  )
}

function OpenRatesStats(props: {
  data: ResultOf<typeof ViewsStatsFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()

  const statsData = createMemo(() => {
    const openRatesStats = props.data.me?.publication?.openRatesStats

    if (!openRatesStats) return null

    const currentMonthEmailsSent = openRatesStats.currentMonthEmailsSent ?? 0

    if (currentMonthEmailsSent === 0) return null

    return {
      count: openRatesStats.currentMonthOpenRatePercent ?? 0,
      previousCount: openRatesStats.previousMonthOpenRatePercent ?? 0,
      intervalType: "DAY" as const,
    }
  })

  return (
    <Show when={statsData()}>
      {data => (
        <Stats
          data={data()}
          loading={props.isPending}
          hue="orange"
          label={LL().dashboard.openRate()}
          iconName="mail"
          format={{ style: "unit", unit: "percent", unitDisplay: "narrow" }}
        />
      )}
    </Show>
  )
}

function ViewsStats(props: {
  data: ResultOf<typeof ViewsStatsFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()

  const statsData = createMemo(() => {
    const dailyViews = props.data.me?.publication?.dailyViews ?? []
    const viewStats = props.data.me?.publication?.viewStats

    const chartData = dailyViews
      .filter(stat => stat.day)
      .map(stat => ({
        date: stat.day,
        count: stat.amount ?? 0,
      }))
      .reverse()

    return {
      count: viewStats?.currentPeriodViews ?? 0,
      previousCount: viewStats?.previousPeriodViews ?? 0,
      chartData,
      intervalType: "DAY" as const,
    }
  })

  return (
    <Stats
      data={statsData()}
      loading={props.isPending}
      hue="orange"
      label={LL().dashboard.views()}
      iconName="eye"
    />
  )
}

const BlogLatestFragment = graphql(
  `fragment BlogLatestFragment on Query {
    latestPublishedPosts: posts(
      first: 2
      status: [published, sent]
      order: { field: publishedAt, direction: desc }
    ) {
      edges { node { ...Post } }
    }
  }`,
  [PostFragment],
)

function BlogLatest(props: {
  data: FragmentOf<typeof BlogLatestFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const fragmentData = createFragment(BlogLatestFragment, () => props.data)
  const posts = createMemo(() =>
    fragmentData().latestPublishedPosts.edges.map(edge => edge.node),
  )

  return (
    <DashboardSection
      title={LL().dashboard.latestPosts()}
      iconName="pen-tool"
      hue="amber"
      link={{
        href: paths({ filter: "published" }).url,
        text: LL().dashboard.viewAllPosts(),
      }}
    >
      <Show
        when={!props.isPending}
        fallback={<Info>{LL().common.loading()}</Info>}
      >
        <Show
          when={posts().length}
          fallback={<Info>{LL().dashboard.noPostsYet()}</Info>}
        >
          <Box gap="12" fill>
            <For each={posts()}>
              {node => <PostComponent postSearch={node} />}
            </For>
          </Box>
        </Show>
      </Show>
    </DashboardSection>
  )
}

const BlogDraftsFragment = graphql(
  `fragment BlogDraftsFragment on Query {
    draftPosts: posts(
      first: 2
      status: [draft]
      order: { field: createdAt, direction: desc }
    ) {
      edges { node { ...Post } }
    }
  }`,
  [PostFragment],
)

function BlogDrafts(props: {
  data: FragmentOf<typeof BlogDraftsFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const fragmentData = createFragment(BlogDraftsFragment, () => props.data)
  const posts = createMemo(() =>
    fragmentData().draftPosts.edges.map(edge => edge.node),
  )

  return (
    <DashboardSection
      title={LL().dashboard.latestDrafts()}
      iconName="pen-tool"
      hue="amber"
      link={{
        href: paths({ filter: "draft" }).url,
        text: LL().dashboard.viewAllDrafts(),
      }}
    >
      <Show
        when={!props.isPending}
        fallback={<Info>{LL().common.loading()}</Info>}
      >
        <Show
          when={posts().length}
          fallback={<Info>{LL().dashboard.noDrafts()}</Info>}
        >
          <Box gap="12" fill>
            <For each={posts()}>
              {node => <PostComponent postSearch={node} />}
            </For>
          </Box>
        </Show>
      </Show>
    </DashboardSection>
  )
}

const LastNotesFragment = graphql(
  `fragment LastNotesFragment on Query {
    latestPostNotes(first: 2) {
      edges {
        node {
          id
          ...NoteFragment
        }
      }
    }
  }`,
  [NoteFragment],
)

function LastNotes(props: {
  data: FragmentOf<typeof LastNotesFragment>
  isPending: boolean
}) {
  const { LL } = useI18nContext()
  const { paths } = createBlogRouter()

  const fragmentData = createFragment(LastNotesFragment, () => props.data)
  const mappedNotes = createMemo(() =>
    fragmentData()
      .latestPostNotes.edges.map(edge => edge.node)
      .filter((n): n is NonNullable<typeof n> => n !== null),
  )

  function getNoteHref(note: FragmentOf<typeof NoteFragment>) {
    const noteData = readFragment(NoteFragment, note)
    const firstPost = noteData.posts?.[0]
    if (!firstPost) return undefined
    const postData = readFragment(PostAttachmentFragment, firstPost)
    return paths()
      .post({ id: postData.id, title: postData.currentVersion?.title ?? null })
      .notes().url
  }

  return (
    <DashboardSection
      title={LL().dashboard.latestNotes()}
      iconName="notebook-text"
      hue="emerald"
    >
      <Show
        when={!props.isPending}
        fallback={<Info>{LL().common.loading()}</Info>}
      >
        <Show
          when={mappedNotes().length}
          fallback={<Info>{LL().dashboard.noNotesYet()}</Info>}
        >
          <Box gap="12" fill>
            <For each={mappedNotes()}>
              {note => (
                <Note
                  note={note}
                  href={getNoteHref(note)}
                  showDelete={false}
                  maxContentHeight={200}
                />
              )}
            </For>
          </Box>
        </Show>
      </Show>
    </DashboardSection>
  )
}

const OnboardingTaskFragment = graphql(`
  fragment OnboardingTaskFragment on OnboardingTask {
    id
    status
  }
`)

function OnboardingTasks(props: {
  onboardingTasksQuery: CreateQueryState<ResultOf<typeof DashboardQuery>>
  data: FragmentOf<typeof OnboardingFragment>
}) {
  const navigate = useNavigate()
  const { paths } = createBlogRouter()
  const { LL } = useI18nContext()

  async function reexecuteOnboardingTasksQuery() {
    await urqlClient.query(DashboardQuery, {}).toPromise()
  }

  const fragmentData = createFragment(OnboardingFragment, () => props.data)

  const latestDraft = createMemo(() => {
    const node = fragmentData().onboardingDraftPosts?.edges[0]?.node
    if (!node) return null

    return {
      id: node.post.id,
      title: node.post.currentVersion?.title ?? null,
    }
  })

  const currentTask = createFragment(
    OnboardingTaskFragment,
    () => fragmentData().me?.currentOnboardingTask,
  )

  const [isCompleting, setIsCompleting] = createSignal(false)
  const [showFireworks, setShowFireworks] = createSignal(false)

  const taskCompleted = async () => {
    const task = currentTask()
    if (!task) return false

    const RefetchOnboardingTaskQuery = graphql(
      `
      query RefetchOnboardingTask($id: OnboardingTaskKey!) {
        onboardingTask(id: $id) {
          status
        }
      }
      `,
    )

    const updatedTask = await urqlClient
      .query(RefetchOnboardingTaskQuery, { id: task.id })
      .toPromise()

    if (updatedTask.data?.onboardingTask?.status === "completed") {
      setIsCompleting(true)
      setShowFireworks(true)
      setTimeout(async () => {
        setShowFireworks(false)
        setIsCompleting(false)
        await reexecuteOnboardingTasksQuery()
      }, 2000)
    }
  }

  const CreatePostMutation = graphql(
    `mutation CreatePostDashboard($id: ID!) {
      createPost(id: $id) {
        ...on MutationCreatePostSuccess {
          data {
            id
          }
        }
        ...ErrorFragment
      }
    }`,
    [ErrorFragment],
  )
  const [_createPostState, createPost] = createMutationWithErrorHandling(
    CreatePostMutation,
    "createPost",
  )
  const SkipOnboardingTaskMutation = graphql(
    `
    mutation SkipOnboardingTask($id: OnboardingTaskKey!) {
      skipOnboardingTask(id: $id) {
        ... on MutationSkipOnboardingTaskSuccess {
          data {
            ...OnboardingTaskFragment
          }
        }
        ...ErrorFragment
      }
    }
  `,
    [ErrorFragment, OnboardingTaskFragment],
  )
  const [skipOnboardingTaskState, skipOnboardingTask] =
    createMutationWithErrorHandling(
      SkipOnboardingTaskMutation,
      "skipOnboardingTask",
    )

  async function onCreatePost() {
    const draft = latestDraft()
    if (draft) {
      navigate(paths().post({ id: draft.id, title: draft.title }).url)
    } else {
      const id = ulid()
      await createPost({ id })
      navigate(paths().post({ id, title: null }).url)
    }
  }

  async function handleIgnore() {
    const task = currentTask()
    if (!task) return

    await skipOnboardingTask({ id: task.id })
    await reexecuteOnboardingTasksQuery()
  }

  const taskData = createMemo(() => {
    const task = currentTask()
    const data = fragmentData()
    if (!task || !data) return null
    return { task, data }
  })

  const wrapperProps = createMemo(() => ({
    isCompleting: isCompleting(),
    showFireworks: showFireworks(),
    loading:
      skipOnboardingTaskState.fetching || props.onboardingTasksQuery.fetching,
  }))

  return (
    <Show when={taskData()}>
      {taskDataValue => (
        <OnboardingTaskWrapper {...wrapperProps()}>
          <Switch>
            <Match when={taskDataValue()?.task.id === "create_first_post"}>
              <Box gap="4">
                <h3 class={onboardingTitleClass}>
                  {LL().onboarding.createFirstPost.title()}
                </h3>
                <p class={onboardingDescriptionClass}>
                  {latestDraft()
                    ? LL().onboarding.createFirstPost.descriptionWithDraft()
                    : LL().onboarding.createFirstPost.description()}
                </p>
              </Box>
              <div class={onboardingActionsClass}>
                <Button kind="surface" hue="orange" onClick={handleIgnore}>
                  {LL().onboarding.createFirstPost.ignore()}
                </Button>
                <Button kind="solid" hue="orange" onClick={onCreatePost}>
                  {latestDraft()
                    ? LL().onboarding.createFirstPost.continueWriting()
                    : LL().onboarding.createFirstPost.createPost()}
                </Button>
              </div>
            </Match>
            <Match when={taskDataValue()?.task.id === "create_second_post"}>
              <Box gap="4">
                <h3 class={onboardingTitleClass}>
                  {LL().onboarding.createSecondPost.title()}
                </h3>
                <p class={onboardingDescriptionClass}>
                  {latestDraft()
                    ? LL().onboarding.createSecondPost.descriptionWithDraft()
                    : LL().onboarding.createSecondPost.description()}
                </p>
              </Box>
              <div class={onboardingActionsClass}>
                <Button kind="surface" hue="orange" onClick={handleIgnore}>
                  {LL().onboarding.createSecondPost.ignore()}
                </Button>
                <Button kind="solid" hue="orange" onClick={onCreatePost}>
                  {_createPostState.fetching
                    ? LL().onboarding.createSecondPost.creating()
                    : latestDraft()
                      ? LL().onboarding.createSecondPost.continueWriting()
                      : LL().onboarding.createSecondPost.createPost()}
                </Button>
              </div>
            </Match>
            <Match when={taskDataValue()?.task.id === "fill_profile"}>
              <Show when={taskDataValue()?.data.me}>
                {user => (
                  <FillProfileTask
                    user={user()}
                    onIgnore={handleIgnore}
                    onAfterSave={async () => {
                      await taskCompleted()
                    }}
                  />
                )}
              </Show>
            </Match>
            <Match when={taskDataValue()?.task.id === "set_language"}>
              <SetLanguageTask
                publication={fragmentData().me!.publication!}
                onIgnore={handleIgnore}
                onAfterSave={async () => {
                  await taskCompleted()
                }}
              />
            </Match>
            <Match when={taskDataValue()?.task.id === "fill_publication"}>
              <FillPublicationTask
                publication={fragmentData().me!.publication!}
                onIgnore={handleIgnore}
                onAfterSave={async () => {
                  await taskCompleted()
                }}
              />
            </Match>
            <Match when={taskDataValue()?.task.id === "translate_articles"}>
              <TranslateArticlesTask onIgnore={handleIgnore} />
            </Match>
          </Switch>
        </OnboardingTaskWrapper>
      )}
    </Show>
  )
}

function OnboardingTaskWrapper(props: {
  children: JSX.Element
  isCompleting: boolean
  showFireworks: boolean
  loading: boolean
}) {
  const { LL } = useI18nContext()

  return (
    <div class={onboardingContainerClass}>
      <div class={onboardingCardClass({ completing: props.isCompleting })}>
        <Show when={props.showFireworks}>
          <Fireworks />
        </Show>
        <img
          src={rabbitOnboarding}
          alt={LL().onboarding.onboardingRabbitAlt()}
          class={onboardingImageClass}
        />
        <div
          class={onboardingContentClass({
            deleting: props.loading,
          })}
        >
          {props.children}
        </div>
      </div>
    </div>
  )
}

function FillProfileTask(props: {
  user: FragmentOf<typeof UserProfileFragment>
  onIgnore: () => void
  onAfterSave?: () => void | Promise<void>
}) {
  const { LL } = useI18nContext()
  const [isOpen, setIsOpen] = createSignal(false)

  async function handleAfterSave() {
    setIsOpen(false)
    await props.onAfterSave?.()
  }

  return (
    <>
      <Box gap="4">
        <h3 class={onboardingTitleClass}>
          {LL().onboarding.fillProfile.title()}
        </h3>
        <p class={onboardingDescriptionClass}>
          {LL().onboarding.fillProfile.description()}
        </p>
      </Box>
      <div class={onboardingActionsClass}>
        <Button kind="surface" hue="orange" onClick={props.onIgnore}>
          {LL().onboarding.fillProfile.ignore()}
        </Button>
        <Button kind="solid" hue="orange" onClick={() => setIsOpen(true)}>
          {LL().onboarding.fillProfile.editProfile()}
        </Button>
      </div>

      <Dialog open={isOpen()} onOpenChange={setIsOpen}>
        <ProfileForm user={props.user} onAfterSave={handleAfterSave} />
      </Dialog>
    </>
  )
}

function SetLanguageTask(props: {
  onIgnore: () => void
  publication: FragmentOf<typeof PublicationLanguageFormFragment>
  onAfterSave?: () => void | Promise<void>
}) {
  const { LL } = useI18nContext()
  const [isOpen, setIsOpen] = createSignal(false)

  async function handleAfterSave() {
    setIsOpen(false)
    await props.onAfterSave?.()
  }

  return (
    <>
      <Box gap="4">
        <h3 class={onboardingTitleClass}>
          {LL().onboarding.setLanguage.title()}
        </h3>
        <p class={onboardingDescriptionClass}>
          {LL().onboarding.setLanguage.description()}
        </p>
      </Box>
      <div class={onboardingActionsClass}>
        <Button kind="surface" hue="orange" onClick={props.onIgnore}>
          {LL().onboarding.setLanguage.ignore()}
        </Button>
        <Button kind="solid" hue="orange" onClick={() => setIsOpen(true)}>
          {LL().onboarding.setLanguage.chooseLanguage()}
        </Button>
      </div>

      <Dialog open={isOpen()} onOpenChange={setIsOpen}>
        <PublicationLanguageForm
          title={LL().onboarding.setLanguage.title()}
          iconName="languages"
          publication={props.publication}
          onAfterSave={handleAfterSave}
        />
      </Dialog>
    </>
  )
}

function FillPublicationTask(props: {
  onIgnore: () => void
  onAfterSave?: () => void | Promise<void>
  publication: FragmentOf<typeof PublicationDetailsFragment>
}) {
  const { LL } = useI18nContext()
  const [isOpen, setIsOpen] = createSignal(false)

  async function handleAfterSave() {
    setIsOpen(false)
    await props.onAfterSave?.()
  }

  return (
    <>
      <Box gap="4">
        <h3 class={onboardingTitleClass}>
          {LL().onboarding.fillPublication.title()}
        </h3>
        <p class={onboardingDescriptionClass}>
          {LL().onboarding.fillPublication.description()}
        </p>
      </Box>
      <div class={onboardingActionsClass}>
        <Button kind="surface" hue="orange" onClick={props.onIgnore}>
          {LL().onboarding.fillPublication.ignore()}
        </Button>
        <Button kind="solid" hue="orange" onClick={() => setIsOpen(true)}>
          {LL().onboarding.fillPublication.editDetails()}
        </Button>
      </div>

      <Dialog open={isOpen()} onOpenChange={setIsOpen}>
        <PublicationDetailsForm
          title={LL().blog.titleAndDescription()}
          iconName="user-cog"
          publication={props.publication}
          onAfterSave={handleAfterSave}
        />
      </Dialog>
    </>
  )
}

function TranslateArticlesTask(props: { onIgnore: () => void }) {
  const { paths } = createBlogRouter()
  const { LL } = useI18nContext()

  const LatestPublishedPostQuery = graphql(
    `query LatestPublishedPostForTranslation {
        posts(
          first: 1
          status: [published, sent]
          order: { field: publishedAt, direction: desc }
        ) {
          edges { node { ...Post } }
        }
      }
    `,
    [PostFragment],
  )

  const [postsQuery] = createQuery({
    query: LatestPublishedPostQuery,
  })

  const latestPost = createMemo(() => {
    const edges = postsQuery.data?.posts.edges
    const node = edges && edges.length > 0 ? edges[0]?.node : null
    return readFragment(PostFragment, node)
  })

  const taskData = createMemo(() => {
    const post = latestPost()
    if (!post) return null

    return {
      postId: post.post.id,
      title: post.post.currentVersion?.title ?? "untitled",
      translationsUrl: paths()
        .post({
          id: post.post.id,
          title: post.post.currentVersion?.title ?? "untitled",
        })
        .translations(),
    }
  })

  return (
    <Show when={taskData()}>
      {data => (
        <>
          <Box gap="4">
            <h3 class={onboardingTitleClass}>
              {LL().onboarding.translateArticles.title({
                title: data().title,
              })}
            </h3>
            <p class={onboardingDescriptionClass}>
              {LL().onboarding.translateArticles.description()}
            </p>
          </Box>
          <div class={onboardingActionsClass}>
            <Button kind="surface" hue="orange" onClick={props.onIgnore}>
              {LL().onboarding.translateArticles.ignore()}
            </Button>
            <LinkButton kind="solid" hue="orange" href={data().translationsUrl}>
              {LL().onboarding.translateArticles.translateLatestArticle()}
            </LinkButton>
          </div>
        </>
      )}
    </Show>
  )
}

function CreatePublicationWidget() {
  const { LL } = useI18nContext()
  const [isOpen, setIsOpen] = createSignal(false)

  return (
    <DashboardSection
      title={LL().dashboard.createPublication.title()}
      iconName="pen-tool"
      hue="amber"
    >
      <Box gap="16" fill>
        <Text fontSize="m" color="sand700">
          {LL().dashboard.createPublication.description()}
        </Text>
        <Box alignSelf="flex-end">
          <Button
            kind="solid"
            hue="amber"
            iconName="arrow-right"
            onClick={() => setIsOpen(true)}
          >
            {LL().dashboard.createPublication.button()}
          </Button>
        </Box>
      </Box>

      <Dialog open={isOpen()} onOpenChange={setIsOpen}>
        <PublicationNameForm
          title={LL().blog.blogName()}
          iconName="user"
          onAfterSave={() => {
            setIsOpen(false)
            window.location.reload()
          }}
        />
      </Dialog>
    </DashboardSection>
  )
}

const OnboardingFragment = graphql(
  `fragment OnboardingFragment on Query {
    me {
      ...UserProfileFragment
      publication {
        ...PublicationLanguageFormFragment
        ...PublicationDetailsFragment
      }
      currentOnboardingTask { 
        ...OnboardingTaskFragment 
      }
    }
    onboardingDraftPosts: posts(
      first: 1
      status: [draft]
      order: { field: createdAt, direction: desc }
    ) {
      edges { 
        node { 
          id
          post {
            id
            currentVersion {
              id
              title
            }
          }
        } 
      }
    }
  }`,
  [
    UserProfileFragment,
    OnboardingTaskFragment,
    PublicationLanguageFormFragment,
    PublicationDetailsFragment,
  ],
)

const DashboardQuery = graphql(
  `query Dashboard {
    ...OnboardingFragment
    ...SubscribersStatsFragment
    ...PostsStatsFragment
    ...ViewsStatsFragment
    ...BlogLatestFragment
    ...BlogDraftsFragment
    ...LastNotesFragment
    ...RecentReactionsFragment
    ...DashboardHeaderFragment
  }`,
  [
    OnboardingFragment,
    SubscribersStatsFragment,
    PostsStatsFragment,
    ViewsStatsFragment,
    BlogLatestFragment,
    BlogDraftsFragment,
    LastNotesFragment,
    RecentReactionsFragment,
    DashboardHeaderFragment,
  ],
)
